/**
 * Message Grouping Utility
 *
 * Determines which messages should display the sender header
 * (avatar + name) in chat views. Sender info appears only on
 * the LAST message of each consecutive group from the same sender.
 * Groups break on sender change or a time gap exceeding the threshold.
 */

/** Minimum gap between two messages from the same sender that
 *  triggers a new group (and thus a header on the preceding message). */
export const MESSAGE_GROUP_TIME_GAP_MS = 5 * 60 * 1000 // 5 minutes

/** Minimal shape needed from a message to compute grouping. */
export interface GroupableMessage {
  id: string
  senderId: string
  createdAt: string // ISO 8601
  isDeleted?: boolean
}

/**
 * Returns the set of message IDs that should display the sender header.
 *
 * Rules (for each non-deleted message M):
 *   - M gets a header when:
 *     a) M is the last non-deleted message in the list, OR
 *     b) the next non-deleted message N has a different senderId than M, OR
 *     c) the time gap between N.createdAt and M.createdAt exceeds
 *        MESSAGE_GROUP_TIME_GAP_MS
 *   - Deleted messages are transparent: they are skipped when looking
 *     for the next comparison message, and they never get a header.
 */
export function getMessagesWithSenderHeader(messages: GroupableMessage[]): Set<string> {
  const result = new Set<string>()

  for (let i = 0; i < messages.length; i++) {
    const current = messages[i]

    // Deleted messages never render a sender header
    if (current.isDeleted) continue

    // Find the next non-deleted message (skip deleted ones)
    let next: GroupableMessage | null = null
    for (let j = i + 1; j < messages.length; j++) {
      if (!messages[j].isDeleted) {
        next = messages[j]
        break
      }
    }

    // Last non-deleted message always gets a header
    if (!next) {
      result.add(current.id)
      continue
    }

    // Different sender → current is last in its group
    if (next.senderId !== current.senderId) {
      result.add(current.id)
      continue
    }

    // Same sender — check time gap
    const gap = new Date(next.createdAt).getTime() - new Date(current.createdAt).getTime()

    if (gap > MESSAGE_GROUP_TIME_GAP_MS) {
      result.add(current.id)
    }
    // If gap <= threshold, same group continues → no header on current
  }

  return result
}
